import React, { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Download, Mail, Lock } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { supabase } from '../lib/supabase';

interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

declare global {
  interface WindowEventMap {
    beforeinstallprompt: BeforeInstallPromptEvent;
  }
}

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [resendingEmail, setResendingEmail] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const { user, signIn } = useAuth();

  React.useEffect(() => {
    const handleBeforeInstallPrompt = (e: BeforeInstallPromptEvent) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt as any);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt as any);
    };
  }, []);

  if (user) {
    return <Navigate to="/calendar" />;
  }

  async function handleInstall() {
    if (!deferredPrompt) return;

    try {
      await deferredPrompt.prompt();
      const choiceResult = await deferredPrompt.userChoice;
      
      if (choiceResult.outcome === 'accepted') {
        setDeferredPrompt(null);
      }
    } catch (err) {
      console.error('Error during installation:', err);
    }
  }

  async function handleResendConfirmation() {
    try {
      setResendingEmail(true);
      const { error } = await supabase.auth.resend({
        type: 'signup',
        email: email,
      });
      
      if (error) throw error;
      
      toast.success('Confirmation email sent! Please check your inbox and spam folder.');
    } catch (error) {
      toast.error('Failed to resend confirmation email. Please try again.');
      console.error('Error resending confirmation:', error);
    } finally {
      setResendingEmail(false);
    }
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    try {
      setLoading(true);
      await signIn(email, password);
    } catch (error: any) {
      if (error?.message?.includes('email not confirmed') || 
          error?.message?.toLowerCase()?.includes('email_not_confirmed')) {
        toast.error(
          <div className="space-y-2">
            <p>Please confirm your email address before signing in.</p>
            <button
              onClick={handleResendConfirmation}
              disabled={resendingEmail}
              className="text-indigo-600 hover:text-indigo-500 text-sm font-medium"
            >
              {resendingEmail ? 'Sending...' : 'Resend confirmation email'}
            </button>
          </div>,
          { duration: 6000 }
        );
      } else {
        toast.error('Failed to sign in');
      }
      console.error('Error signing in:', error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="min-h-screen flex items-start pt-12 sm:items-center justify-center bg-gradient-to-br from-slate-50 via-gray-50 to-stone-100 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full">
        <div className="bg-white rounded-2xl shadow-xl border border-gray-100 p-8 sm:p-10">
          <div className="flex flex-col items-center">
            <div className="flex items-center justify-center p-4 mb-2">
              <span className="text-4xl font-extrabold tracking-tight select-none" style={{ fontFamily: 'sans-serif' }}>
                <span className="text-[#4a3f35]">
                  <span className="text-5xl">F</span>ARM
                </span>
                <span className="text-[#8cc63f]">
                  <span className="text-5xl">A</span>RK
                </span>
              </span>
            </div>
            <h2 className="mt-2 text-center text-2xl font-bold text-gray-900">
              Sign in to your account
            </h2>
            <p className="mt-2 text-center text-sm text-gray-500">
              Welcome back! Please enter your details.
            </p>
          </div>

          <form className="mt-8 space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="email-address" className="block text-sm font-medium text-gray-700 mb-1.5">
                Email address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="email-address"
                  name="email"
                  type="email"
                  autoComplete="email"
                  required
                  className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#8cc63f] focus:border-transparent sm:text-sm transition-shadow"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                />
              </div>
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Lock className="h-4 w-4 text-gray-400" />
                </div>
                <input
                  id="password"
                  name="password"
                  type="password"
                  autoComplete="current-password"
                  required
                  className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-200 rounded-lg placeholder-gray-400 text-gray-900 focus:outline-none focus:ring-2 focus:ring-[#8cc63f] focus:border-transparent sm:text-sm transition-shadow"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                />
              </div>
            </div>

            <div className="flex flex-col space-y-3 pt-2">
              <button
                type="submit"
                disabled={loading}
                className="w-full flex justify-center py-2.5 px-4 border border-transparent text-sm font-semibold rounded-lg text-white bg-[#4a3f35] hover:bg-[#3d342c] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#4a3f35] disabled:opacity-50 disabled:cursor-not-allowed transition-colors shadow-sm"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </button>
              
              {deferredPrompt && (
                <button
                  type="button"
                  onClick={handleInstall}
                  className="w-full flex justify-center items-center py-2.5 px-4 border border-gray-200 text-sm font-medium rounded-lg text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-200 transition-colors"
                >
                  <Download className="h-4 w-4 mr-2 text-gray-500" />
                  Install App
                </button>
              )}
            </div>
          </form>
        </div>
        
        <p className="mt-6 text-center text-xs text-gray-400">
          Farmark. All rights reserved.
        </p>
      </div>
    </div>
  );
}
